import {
  type AnnulOperation,
  type BranchChangeset,
  isReconciliationSpecCreateOperation,
  type ReconciliationSpecCreateOperation,
} from '@festina/configuration-general-types/configuration'
import type { Tree } from '@festina-life/api'
import { dateUtils, type Optional } from '@festina-life/core'

import type { ConfigurationItemsByBlock } from '../../workspace/operationsContext'
import { createOperationFindFn } from '../helpers'
import { constructIndexedOperations } from '../indexedOperations/helpers'
import type { WorkspaceOperationOf } from '../types'
import type {
  IndexedReconciliationSpecOperations,
  IndexedReconciliationSpecOperationWithMetaData,
} from './types'

export const getReconciliationSpecOperationFromDate = (
  operation: ReconciliationSpecCreateOperation,
) => operation.reconciliationSpec?.from

export const reconciliationSpecOperationFindFn = createOperationFindFn(
  (operation: IndexedReconciliationSpecOperationWithMetaData) => {
    const fromDate = getReconciliationSpecOperationFromDate(
      operation.internalOperation,
    )
    return fromDate
      ? dateUtils.dateUtcAsLocal(fromDate).valueOf()
      : Number.NEGATIVE_INFINITY
  },
)

/**
 * A reconciliation spec only applies at the given date once at least one of its
 * versions has come into effect. Versions without a `from` date are always in
 * effect.
 */
export const isReconciliationSpecActiveAtDate =
  (viewAt: Date) => (value: Optional<IndexedReconciliationSpecOperations>) =>
    !!value?.operations.some((operation) => {
      const fromDate = getReconciliationSpecOperationFromDate(
        operation.internalOperation,
      )

      return (
        !fromDate ||
        dateUtils.dateUtcAsLocal(fromDate).valueOf() <= viewAt.getTime()
      )
    })

/**
 * Resolves the reconciliation-spec version that is in effect at the given date,
 * i.e. the last operation whose `from` date is not in the future relative to
 * `atDate`.
 */
export const getReconciliationSpecOperationAtDate = (
  indexedReconciliationSpecOperations: Optional<IndexedReconciliationSpecOperations>,
  atDate: Date,
) => {
  const comparableDate = atDate.getTime()

  return (indexedReconciliationSpecOperations?.operations ?? []).findLast(
    (compareTo) => {
      const compareToFromDate = getReconciliationSpecOperationFromDate(
        compareTo.internalOperation,
      )
      return (
        !compareToFromDate ||
        dateUtils.dateUtcAsLocal(compareToFromDate).valueOf() <= comparableDate
      )
    },
  )
}

export const constructIndexedReconciliationSpecOperations = ({
  configurationChangesets,
  draftReconciliationSpecOperations,
  draftAnnulOperations,
  blockTopology,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  draftReconciliationSpecOperations: Optional<
    WorkspaceOperationOf<ReconciliationSpecCreateOperation>[]
  >
  draftAnnulOperations?: WorkspaceOperationOf<AnnulOperation>[]
  blockTopology: Tree
}): ConfigurationItemsByBlock<IndexedReconciliationSpecOperations> =>
  constructIndexedOperations<
    ReconciliationSpecCreateOperation,
    IndexedReconciliationSpecOperations
  >({
    configurationChangesets,
    draftOperations: draftReconciliationSpecOperations,
    draftAnnulOperations,
    blockTopology,
    isIndexedOperation: isReconciliationSpecCreateOperation,
    getEntityId: (operation) => operation.reconciliationSpec?.specId,
    createDefault: (specId) =>
      ({
        specId,
        // These fields are updated once all operations have been added.
        lastEntity: undefined,
        isDraft: false,
        operations: [],
      }) satisfies IndexedReconciliationSpecOperations,
    findIndexFn: reconciliationSpecOperationFindFn,
    postProcessEntity: (
      indexedReconciliationSpec: IndexedReconciliationSpecOperations,
    ) => {
      indexedReconciliationSpec.lastEntity =
        indexedReconciliationSpec.operations.at(
          -1,
        )?.internalOperation.reconciliationSpec
    },
  })
